import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { get_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import PayoutDetailsPopup from './popups/PayoutDetailsPopup';
import AddPenaltyPopup from './popups/AddPenaltyPopup';
import AddIncentivePopup from './popups/AddIncentivePopup';
import AddWaiverPopup from './popups/AddWaiverPopup';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const formatCurrency = (amount, currency) => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency || 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const getMonthName = (monthValue) => {
  const monthObj = MONTHS.find((m) => m.value === parseInt(monthValue, 10));
  return monthObj ? monthObj.label : '';
};

const getStatusConfig = (status) => {
  switch (status) {
    case 'FINISHED':
      return { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Finished' };
    case 'CANCELLED':
      return { bg: 'bg-red-600', text: 'text-white', label: 'Cancelled' };
    case 'NO_SHOW':
      return { bg: 'bg-amber-500', text: 'text-white', label: 'No Show' };
    default:
      return { bg: 'bg-gray-500', text: 'text-white', label: status };
  }
};

class TeacherPayoutDetail extends Component {
  state = {
    loading: true,
    teacherName: '',
    teacherUuid: '',
    month: '',
    year: '',
    totalSessions: 0,
    sessionDetails: [],
    showDetailsPopup: false,
    detailsPopupType: '',
    detailsPopupSessionUuid: '',
    showAddPenaltyPopup: false,
    showAddIncentivePopup: false,
    showAddWaiverPopup: false,
    addPopupSessionUuid: ''
  };

  componentDidMount() {
    this.loadData();
  }

  loadData = () => {
    const { teacher_uuid, month, year } = this.props.match.params;
    this.setState({ loading: true, teacherUuid: teacher_uuid, month, year });

    const url = get_api_url(
      `/v2/cms_user/teachers/payout-details/teacher/${teacher_uuid}/time/${month}/${year}`,
      null,
      true
    );

    get_api(url, true)
      .then((response) => {
        this.setState({
          loading: false,
          teacherName: response.data.teacher_name || '',
          totalSessions: response.data.total_sessions || 0,
          sessionDetails: response.data.session_details || []
        });
      })
      .catch((error) => {
        console.error('Error fetching teacher payout details:', error);
        this.setState({ loading: false, sessionDetails: [] });
      });
  };

  downloadCSV = () => {
    const { sessionDetails, teacherUuid, month, year } = this.state;
    if (sessionDetails.length === 0) return;

    const headers = [
      'Session UUID',
      'Date/Time',
      'Type',
      'Status',
      'Duration (min)',
      'Class Name',
      'Session Payout',
      'Penalties',
      'Incentives',
      'Waivers',
      'Final Payout',
      'Currency'
    ];

    const rows = sessionDetails.map((session) => [
      session.session_uuid,
      session.date_time,
      session.type,
      session.status,
      session.session_duration,
      session.session_class_name,
      session.session_payout,
      session.total_penalty,
      session.total_incentives,
      session.total_waivers,
      session.final_payout,
      session.currency
    ]);

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `teacher_payout_details_${teacherUuid}_${month}_${year}.csv`);
  };

  openSessionPage = (session) => {
    if (session.type === 'GROUP_SESSION') {
      window.open(`/groupsessiondetails?session_uuid=${session.session_uuid}`, '_blank');
    } else {
      window.open(`/sessiondetails?id=${session.session_uuid}`, '_blank');
    }
  };

  openDetailsPopup = (type, sessionUuid) => {
    this.setState({
      showDetailsPopup: true,
      detailsPopupType: type,
      detailsPopupSessionUuid: sessionUuid
    });
  };

  closeDetailsPopup = () => {
    this.setState({
      showDetailsPopup: false,
      detailsPopupType: '',
      detailsPopupSessionUuid: ''
    });
  };

  openAddPopup = (type, sessionUuid) => {
    if (type === 'penalty') {
      this.setState({ showAddPenaltyPopup: true, addPopupSessionUuid: sessionUuid });
    } else if (type === 'incentive') {
      this.setState({ showAddIncentivePopup: true, addPopupSessionUuid: sessionUuid });
    } else if (type === 'waiver') {
      this.setState({ showAddWaiverPopup: true, addPopupSessionUuid: sessionUuid });
    }
  };

  closeAddPopup = (type) => {
    if (type === 'penalty') {
      this.setState({ showAddPenaltyPopup: false, addPopupSessionUuid: '' });
    } else if (type === 'incentive') {
      this.setState({ showAddIncentivePopup: false, addPopupSessionUuid: '' });
    } else if (type === 'waiver') {
      this.setState({ showAddWaiverPopup: false, addPopupSessionUuid: '' });
    }
  };

  onAddSuccess = () => {
    this.loadData();
  };

  calculateSummary = () => {
    const { sessionDetails } = this.state;
    return sessionDetails.reduce(
      (acc, session) => ({
        totalPayout: acc.totalPayout + (session.session_payout || 0),
        totalPenalties: acc.totalPenalties + (session.total_penalty || 0),
        totalIncentives: acc.totalIncentives + (session.total_incentives || 0),
        totalWaivers: acc.totalWaivers + (session.total_waivers || 0),
        totalFinal: acc.totalFinal + (session.final_payout || 0),
        totalDuration: acc.totalDuration + (session.session_duration || 0),
        yogaCount: acc.yogaCount + (session.type !== 'GROUP_SESSION' ? 1 : 0),
        groupCount: acc.groupCount + (session.type === 'GROUP_SESSION' ? 1 : 0)
      }),
      {
        totalPayout: 0,
        totalPenalties: 0,
        totalIncentives: 0,
        totalWaivers: 0,
        totalFinal: 0,
        totalDuration: 0,
        yogaCount: 0,
        groupCount: 0
      }
    );
  };

  renderSummarySection = () => {
    const summary = this.calculateSummary();
    const totalHours = Math.round((summary.totalDuration / 60) * 10) / 10;

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 mb-4">
        {/* Stats Row */}
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 sm:gap-3">
          {/* Sessions Count */}
          <div className="bg-blue-600 rounded-lg p-2 sm:p-2.5 text-center">
            <p className="text-xs text-blue-100 font-medium">Hours</p>
            <p className="text-base sm:text-lg font-bold text-white">{totalHours}</p>
          </div>
          {/* 1:1 Sessions */}
          <div className="bg-indigo-600 rounded-lg p-2 sm:p-2.5 text-center">
            <p className="text-xs text-indigo-100 font-medium">1:1</p>
            <p className="text-base sm:text-lg font-bold text-white">{summary.yogaCount}</p>
          </div>
          {/* Group Sessions */}
          <div className="bg-violet-100 rounded-lg p-2 sm:p-2.5 text-center">
            <p className="text-xs text-violet-600 font-medium">Group</p>
            <p className="text-base sm:text-lg font-bold text-violet-800">{summary.groupCount}</p>
          </div>
          {/* Payout */}
          <div className="bg-slate-200 rounded-lg p-2 sm:p-2.5 text-center">
            <p className="text-xs text-slate-500 font-medium">Payout</p>
            <p className="text-sm sm:text-base font-bold text-slate-800 truncate">
              {formatCurrency(summary.totalPayout, 'INR')}
            </p>
          </div>
          {/* Penalties */}
          <div className="bg-red-600 rounded-lg p-2 sm:p-2.5 text-center">
            <p className="text-xs text-red-100 font-medium">Penalties</p>
            <p className="text-sm sm:text-base font-bold text-white truncate">
              −{formatCurrency(summary.totalPenalties, 'INR')}
            </p>
          </div>
          {/* Incentives */}
          <div className="bg-emerald-100 rounded-lg p-2 sm:p-2.5 text-center">
            <p className="text-xs text-emerald-600 font-medium">Incentives</p>
            <p className="text-sm sm:text-base font-bold text-emerald-800 truncate">
              +{formatCurrency(summary.totalIncentives, 'INR')}
            </p>
          </div>
          {/* Waivers */}
          <div className="bg-amber-100 rounded-lg p-2 sm:p-2.5 text-center">
            <p className="text-xs text-amber-600 font-medium">Waivers</p>
            <p className="text-sm sm:text-base font-bold text-amber-800 truncate">
              {formatCurrency(summary.totalWaivers, 'INR')}
            </p>
          </div>
          {/* Final */}
          <div className="bg-purple-600 rounded-lg p-2 sm:p-2.5 text-center">
            <p className="text-xs text-purple-100 font-medium">Final</p>
            <p className="text-sm sm:text-base font-bold text-white truncate">
              {formatCurrency(summary.totalFinal, 'INR')}
            </p>
          </div>
        </div>
      </div>
    );
  };

  renderEmptyState = () => (
    <div
      className="bg-white rounded-lg border border-gray-200 py-12 px-4 text-center"
      role="status"
    >
      <svg
        className="w-12 h-12 mx-auto mb-3 text-gray-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
        />
      </svg>
      <p className="text-gray-600 font-medium">No Sessions Found</p>
      <p className="text-gray-500 text-sm mt-1">No session data available for this period.</p>
    </div>
  );

  renderMobileCards = () => {
    const { sessionDetails } = this.state;

    return (
      <div className="space-y-3 lg:hidden" role="list" aria-label="Session payout details">
        {sessionDetails.map((session) => {
          const statusConfig = getStatusConfig(session.status);
          return (
            <article
              key={session.session_uuid}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden"
            >
              {/* Card Header - Compact */}
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <time
                    className="text-sm font-semibold text-gray-900"
                    dateTime={session.date_time}
                  >
                    {moment(session.date_time).format('DD MMM, HH:mm')}
                  </time>
                  <span className="text-gray-400">|</span>
                  <span className="text-sm text-gray-600 truncate">
                    {session.type === 'GROUP_SESSION' ? 'Group' : '1:1'}
                  </span>
                </div>
                <span
                  className={`px-2 py-0.5 text-xs font-semibold rounded ${statusConfig.bg} ${statusConfig.text}`}
                >
                  {statusConfig.label}
                </span>
              </div>

              {/* Card Body - Compact */}
              <div className="px-3 py-2">
                <p className="text-sm font-medium text-gray-800 truncate mb-2">
                  {session.session_class_name || 'N/A'} • {session.session_duration}min
                </p>

                {/* Financial Grid - Tight spacing */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mb-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Payout</span>
                    <span className="font-medium text-gray-900">
                      {formatCurrency(session.session_payout, session.currency)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Penalty</span>
                    <span className="font-medium text-red-700">
                      {formatCurrency(session.total_penalty, session.currency)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Incentive</span>
                    <span className="font-medium text-emerald-700">
                      {formatCurrency(session.total_incentives, session.currency)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Waiver</span>
                    <span className="font-medium text-amber-700">
                      {formatCurrency(session.total_waivers, session.currency)}
                    </span>
                  </div>
                </div>

                {/* Final Payout - Highlighted */}
                <div className="flex justify-between items-center py-1.5 px-2 bg-blue-50 rounded mb-2">
                  <span className="text-sm font-semibold text-blue-900">Final Payout</span>
                  <span className="text-base font-bold text-blue-900">
                    {formatCurrency(session.final_payout, session.currency)}
                  </span>
                </div>

                {/* Action Buttons - Compact row */}
                <div className="flex flex-wrap gap-1.5 mb-2">
                  <button
                    type="button"
                    onClick={() => this.openDetailsPopup('penalties', session.session_uuid)}
                    className="px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                  >
                    View Penalties
                  </button>
                  <button
                    type="button"
                    onClick={() => this.openDetailsPopup('incentives', session.session_uuid)}
                    className="px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                  >
                    View Incentives
                  </button>
                  <button
                    type="button"
                    onClick={() => this.openDetailsPopup('waivers', session.session_uuid)}
                    className="px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                  >
                    View Waivers
                  </button>
                </div>

                {/* Add Buttons - Compact row */}
                <div className="flex flex-wrap gap-1.5 mb-2">
                  <button
                    type="button"
                    onClick={() => this.openAddPopup('penalty', session.session_uuid)}
                    className="px-2 py-1 text-xs font-medium text-white bg-emerald-600 rounded hover:bg-emerald-700 transition-colors"
                  >
                    + Penalty
                  </button>
                  <button
                    type="button"
                    onClick={() => this.openAddPopup('incentive', session.session_uuid)}
                    className="px-2 py-1 text-xs font-medium text-white bg-emerald-600 rounded hover:bg-emerald-700 transition-colors"
                  >
                    + Incentive
                  </button>
                  <button
                    type="button"
                    onClick={() => this.openAddPopup('waiver', session.session_uuid)}
                    className="px-2 py-1 text-xs font-medium text-white bg-emerald-600 rounded hover:bg-emerald-700 transition-colors"
                  >
                    + Waiver
                  </button>
                </div>

                {/* View Session Button */}
                <button
                  type="button"
                  onClick={() => this.openSessionPage(session)}
                  className="w-full py-2 text-sm font-semibold text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors flex items-center justify-center gap-1"
                >
                  View Session
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </button>
              </div>
            </article>
          );
        })}
      </div>
    );
  };

  renderDesktopTable = () => {
    const { sessionDetails } = this.state;

    return (
      <div className="hidden lg:block bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" role="table">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200">
                <th
                  scope="col"
                  className="px-3 py-2.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wide"
                >
                  Date/Time
                </th>
                <th
                  scope="col"
                  className="px-3 py-2.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wide"
                >
                  Type
                </th>
                <th
                  scope="col"
                  className="px-3 py-2.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wide"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="px-3 py-2.5 text-center text-xs font-bold text-gray-700 uppercase tracking-wide"
                >
                  Duration
                </th>
                <th
                  scope="col"
                  className="px-3 py-2.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wide"
                >
                  Class
                </th>
                <th
                  scope="col"
                  className="px-3 py-2.5 text-right text-xs font-bold text-gray-700 uppercase tracking-wide"
                >
                  Payout
                </th>
                <th
                  scope="col"
                  className="px-3 py-2.5 text-right text-xs font-bold text-gray-700 uppercase tracking-wide"
                >
                  Penalties
                </th>
                <th
                  scope="col"
                  className="px-3 py-2.5 text-right text-xs font-bold text-gray-700 uppercase tracking-wide"
                >
                  Incentives
                </th>
                <th
                  scope="col"
                  className="px-3 py-2.5 text-right text-xs font-bold text-gray-700 uppercase tracking-wide"
                >
                  Waivers
                </th>
                <th
                  scope="col"
                  className="px-3 py-2.5 text-right text-xs font-bold text-gray-700 uppercase tracking-wide"
                >
                  Final
                </th>
                <th
                  scope="col"
                  className="px-3 py-2.5 text-center text-xs font-bold text-gray-700 uppercase tracking-wide"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sessionDetails.map((session, index) => {
                const statusConfig = getStatusConfig(session.status);
                return (
                  <tr
                    key={session.session_uuid}
                    className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}
                  >
                    <td className="px-3 py-2.5 whitespace-nowrap font-medium text-gray-900">
                      {moment(session.date_time).format('DD MMM, HH:mm')}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-gray-700">
                      {session.type === 'GROUP_SESSION' ? 'Group' : '1:1'}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <span
                        className={`px-2 py-0.5 text-xs font-semibold rounded ${statusConfig.bg} ${statusConfig.text}`}
                      >
                        {statusConfig.label}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-center text-gray-700">
                      {session.session_duration}min
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-gray-700 max-w-[150px] truncate">
                      {session.session_class_name || 'N/A'}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-right font-medium text-gray-900">
                      {formatCurrency(session.session_payout, session.currency)}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-right">
                      <div className="flex flex-col items-end gap-1">
                        <span className="font-semibold text-red-700">
                          {formatCurrency(session.total_penalty, session.currency)}
                        </span>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => this.openDetailsPopup('penalties', session.session_uuid)}
                            className="px-2 py-0.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                          >
                            view
                          </button>
                          <button
                            type="button"
                            onClick={() => this.openAddPopup('penalty', session.session_uuid)}
                            className="px-2 py-0.5 text-xs font-medium text-white bg-emerald-600 rounded hover:bg-emerald-700"
                          >
                            add
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-right">
                      <div className="flex flex-col items-end gap-1">
                        <span className="font-semibold text-emerald-700">
                          {formatCurrency(session.total_incentives, session.currency)}
                        </span>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() =>
                              this.openDetailsPopup('incentives', session.session_uuid)
                            }
                            className="px-2 py-0.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                          >
                            view
                          </button>
                          <button
                            type="button"
                            onClick={() => this.openAddPopup('incentive', session.session_uuid)}
                            className="px-2 py-0.5 text-xs font-medium text-white bg-emerald-600 rounded hover:bg-emerald-700"
                          >
                            add
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-right">
                      <div className="flex flex-col items-end gap-1">
                        <span className="font-semibold text-amber-700">
                          {formatCurrency(session.total_waivers, session.currency)}
                        </span>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => this.openDetailsPopup('waivers', session.session_uuid)}
                            className="px-2 py-0.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                          >
                            view
                          </button>
                          <button
                            type="button"
                            onClick={() => this.openAddPopup('waiver', session.session_uuid)}
                            className="px-2 py-0.5 text-xs font-medium text-white bg-emerald-600 rounded hover:bg-emerald-700"
                          >
                            add
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-right">
                      <span className="font-bold text-gray-900">
                        {formatCurrency(session.final_payout, session.currency)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-center">
                      <button
                        type="button"
                        onClick={() => this.openSessionPage(session)}
                        className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                      >
                        View →
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  renderLoadingState = () => (
    <div className="flex flex-col items-center justify-center py-12" role="status">
      <svg
        className="animate-spin h-8 w-8 text-blue-600 mb-3"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
          fill="none"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      <p className="text-gray-600 text-sm font-medium">Loading sessions...</p>
    </div>
  );

  render() {
    const {
      loading,
      teacherName,
      month,
      year,
      totalSessions,
      sessionDetails,
      showDetailsPopup,
      detailsPopupType,
      detailsPopupSessionUuid,
      showAddPenaltyPopup,
      showAddIncentivePopup,
      showAddWaiverPopup,
      addPopupSessionUuid
    } = this.state;

    return (
      <main className="bg-gray-100 min-h-screen">
        <div className="max-w-7xl mx-auto px-3 py-3 sm:px-4 sm:py-4">
          {/* Header - Compact */}
          <header className="mb-3 sm:mb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
              <div className="min-w-0">
                <button
                  type="button"
                  onClick={() => window.history.back()}
                  className="inline-flex items-center px-3 py-1 text-sm text-white bg-gray-600 hover:bg-gray-700 font-medium rounded mb-1"
                >
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  Back
                </button>
                <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                  {teacherName || 'Teacher'} - Payout Details
                </h1>
                <p className="text-sm text-gray-600">
                  {getMonthName(month)} {year} • {totalSessions} sessions
                </p>
              </div>
              {sessionDetails.length > 0 && (
                <button
                  type="button"
                  onClick={this.downloadCSV}
                  className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors gap-2 shrink-0"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Download CSV
                </button>
              )}
            </div>
          </header>

          {/* Content */}
          {loading ? (
            this.renderLoadingState()
          ) : sessionDetails.length === 0 ? (
            this.renderEmptyState()
          ) : (
            <>
              {this.renderSummarySection()}
              {this.renderMobileCards()}
              {this.renderDesktopTable()}
            </>
          )}

          {/* Popups */}
          {showDetailsPopup && (
            <PayoutDetailsPopup
              type={detailsPopupType}
              sessionUuid={detailsPopupSessionUuid}
              onClose={this.closeDetailsPopup}
            />
          )}
          {showAddPenaltyPopup && (
            <AddPenaltyPopup
              sessionUuid={addPopupSessionUuid}
              onClose={() => this.closeAddPopup('penalty')}
              onSuccess={this.onAddSuccess}
            />
          )}
          {showAddIncentivePopup && (
            <AddIncentivePopup
              sessionUuid={addPopupSessionUuid}
              onClose={() => this.closeAddPopup('incentive')}
              onSuccess={this.onAddSuccess}
            />
          )}
          {showAddWaiverPopup && (
            <AddWaiverPopup
              sessionUuid={addPopupSessionUuid}
              onClose={() => this.closeAddPopup('waiver')}
              onSuccess={this.onAddSuccess}
            />
          )}
        </div>
      </main>
    );
  }
}

export default withRouter(TeacherPayoutDetail);
